import React, {
  memo,
  useCallback,
  startTransition,
  useDeferredValue,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Container,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  ThemeProvider,
  Typography,
  createTheme,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CloudOffRoundedIcon from '@mui/icons-material/CloudOffRounded';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { Link, useNavigate } from 'react-router-dom';
import { CRM_VIEW_BLOCKED_MESSAGE, fetchCrmCustomers } from '../api/crmCustomers';
import { formatEnumLabel, formatValue } from '../utils/crmFormatting';
import { pageColors } from './HomePage';

const CRM_THEME = createTheme({
  palette: {
    primary: {
      main: pageColors.customers,
    },
  },
  typography: {
    fontFamily: 'Inter, Roboto, sans-serif',
  },
});

const PAGE_BACKGROUND =
  'radial-gradient(circle at top left, rgba(0,137,123,0.16), transparent 55%), #f5f7fb';

const SURFACE_SX = {
  borderRadius: 4,
  backgroundColor: '#fff',
  boxShadow: '0 18px 40px rgba(0,0,0,0.07)',
  border: '1px solid rgba(0,0,0,0.05)',
};

const FILTER_FIELD_SX = {
  width: { xs: '100%', sm: 320 },
  flex: { sm: '0 0 auto' },
};

const SELECT_FILTER_FIELD_SX = {
  width: { xs: '100%', sm: 180 },
  flex: { sm: '0 0 auto' },
};

const DESKTOP_ROWS_PER_PAGE = 24;
const MOBILE_ROWS_PER_PAGE = 12;
const CUSTOMER_DETAIL_BACK_STATE = {
  backTo: '/customers',
  backLabel: 'Back to Customers',
};

const BADGE_SX = {
  display: 'inline-flex',
  alignItems: 'center',
  minHeight: 24,
  px: 1,
  borderRadius: '999px',
  backgroundColor: 'rgba(0,137,123,0.08)',
  border: '1px solid rgba(0,137,123,0.16)',
  color: '#00695c',
  fontSize: '0.75rem',
  fontWeight: 600,
  lineHeight: 1.1,
};

const EMPTY_BADGE_SX = {
  color: 'text.secondary',
  fontSize: '0.875rem',
  lineHeight: 1.1,
};

const TABLE_HEADER_SX = {
  fontWeight: 700,
  whiteSpace: 'nowrap',
};

const TABLE_SX = {
  tableLayout: 'fixed',
  width: '100%',
  minWidth: 960,
};

const TABLE_BODY_CELL_SX = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const TABLE_COLUMN_WIDTHS = {
  customer: { width: '23%' },
  customerNumber: { width: '13%' },
  email: { width: '21%' },
  phone: { width: '13%' },
  priority: { width: '10%' },
  segment: { width: '10%' },
  open: { width: '10%' },
};

function isCrmDisabledError(error) {
  return error?.status === 403 && error?.message === 'CRM is disabled for this tenant';
}

function isCrmViewBlockedError(error) {
  return error?.status === 409 && error?.message === CRM_VIEW_BLOCKED_MESSAGE;
}

function formatRangeLabel(page, rowsPerPage, totalRows) {
  if (totalRows <= 0) {
    return 'Showing 0 results';
  }

  const startIndex = (page - 1) * rowsPerPage + 1;
  const endIndex = Math.min(page * rowsPerPage, totalRows);
  return `Showing ${startIndex}-${endIndex} of ${totalRows}`;
}

function compareSortValues(valueA, valueB, order) {
  const aEmpty = valueA === null || valueA === undefined || valueA === '';
  const bEmpty = valueB === null || valueB === undefined || valueB === '';

  if (aEmpty && !bEmpty) return 1;
  if (!aEmpty && bEmpty) return -1;
  if (aEmpty && bEmpty) return 0;

  const numericValueA = Number(valueA);
  const numericValueB = Number(valueB);
  const bothNumeric = Number.isFinite(numericValueA) && Number.isFinite(numericValueB);

  if (bothNumeric) {
    return order === 'asc'
      ? numericValueA - numericValueB
      : numericValueB - numericValueA;
  }

  const stringValueA = String(valueA).toLowerCase();
  const stringValueB = String(valueB).toLowerCase();

  if (stringValueA < stringValueB) return order === 'asc' ? -1 : 1;
  if (stringValueA > stringValueB) return order === 'asc' ? 1 : -1;

  return 0;
}

function buildFilterOptions(rows, valueKey, labelKey) {
  const optionsByValue = new Map();

  rows.forEach((row) => {
    const value = row[valueKey];
    if (!value || optionsByValue.has(value)) {
      return;
    }

    optionsByValue.set(value, {
      value,
      label: row[labelKey],
    });
  });

  return Array.from(optionsByValue.values()).sort((left, right) =>
    left.label.localeCompare(right.label)
  );
}

function buildCustomerRow(customer, index) {
  const customerNumber = String(customer?.customerNumber ?? '').trim();
  const name = String(customer?.name ?? '').trim() || customerNumber || `Customer ${index + 1}`;
  const email = String(customer?.email ?? '').trim();
  const phone = String(customer?.phone ?? '').trim();
  const priority = String(customer?.priority ?? '').trim();
  const segment = String(customer?.segment ?? '').trim();
  const priorityLabel = formatEnumLabel(priority);
  const segmentLabel = formatEnumLabel(segment);

  return {
    key: customerNumber || `${name}-${index}`,
    customerNumber,
    customerNumberSort: customerNumber,
    name,
    nameSort: name,
    email,
    emailSort: email,
    phone,
    phoneSort: phone,
    priority,
    priorityLabel,
    prioritySort: priority ? priorityLabel : '',
    segment,
    segmentLabel,
    segmentSort: segment ? segmentLabel : '',
    searchIndex: [customerNumber, name, email, phone].join(' ').toLowerCase(),
  };
}

function DirectoryBadge({ value }) {
  if (!value) {
    return (
      <Typography component="span" sx={EMPTY_BADGE_SX}>
        Not set
      </Typography>
    );
  }

  return (
    <Box component="span" sx={BADGE_SX}>
      {formatEnumLabel(value)}
    </Box>
  );
}

const CustomerDirectoryResults = memo(function CustomerDirectoryResults({
  isMobile,
  onPageChange,
  onRequestSort,
  order,
  orderBy,
  page,
  rows,
  rowsPerPage,
  totalPages,
  totalRows,
}) {
  if (totalRows === 0) {
    return (
      <Paper elevation={0} sx={{ ...SURFACE_SX, p: { xs: 2, md: 2.5 } }}>
        <Typography variant="h6" fontWeight={700}>
          No customers found
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
          Try a broader search across customer number, name, email, or phone.
        </Typography>
      </Paper>
    );
  }

  if (isMobile) {
    return (
      <Stack spacing={1.5}>
        {rows.map((row) => (
          <Paper key={row.key} elevation={0} sx={{ ...SURFACE_SX, p: 2 }}>
            <Stack spacing={1.25}>
              <Stack direction="row" justifyContent="space-between" spacing={1.5} alignItems="flex-start">
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle1" fontWeight={700} noWrap>
                    {row.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Customer #{formatValue(row.customerNumber)}
                  </Typography>
                </Box>
                <Button
                  component={Link}
                  to={`/customers/${encodeURIComponent(row.customerNumber)}`}
                  state={CUSTOMER_DETAIL_BACK_STATE}
                  size="small"
                  variant="outlined"
                  endIcon={<ArrowForwardRoundedIcon fontSize="small" />}
                  sx={{ flexShrink: 0, borderRadius: '999px', textTransform: 'none' }}
                >
                  Open
                </Button>
              </Stack>

              <Typography variant="body2" color="text.secondary">
                Email: {formatValue(row.email)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Phone: {formatValue(row.phone)}
              </Typography>

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <DirectoryBadge value={row.priority} />
                <DirectoryBadge value={row.segment} />
              </Stack>
            </Stack>
          </Paper>
        ))}

        <Paper elevation={0} sx={{ ...SURFACE_SX, p: { xs: 2, md: 2.25 } }}>
          <Stack spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
            <Typography variant="body2" color="text.secondary" textAlign={{ sm: 'center' }}>
              {formatRangeLabel(page, rowsPerPage, totalRows)}
            </Typography>
            {totalPages > 1 && (
              <Pagination
                count={totalPages}
                page={page}
                onChange={onPageChange}
                color="primary"
                size="small"
                siblingCount={isMobile ? 0 : 1}
                sx={{ alignSelf: 'center' }}
              />
            )}
          </Stack>
        </Paper>
      </Stack>
    );
  }

  return (
    <Stack spacing={1.5}>
      <Paper elevation={0} sx={SURFACE_SX}>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={TABLE_SX}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...TABLE_HEADER_SX, ...TABLE_COLUMN_WIDTHS.customer }}>
                  <TableSortLabel
                    active={orderBy === 'name'}
                    direction={orderBy === 'name' ? order : 'asc'}
                    onClick={() => onRequestSort('name')}
                  >
                    Customer
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ ...TABLE_HEADER_SX, ...TABLE_COLUMN_WIDTHS.customerNumber }}>
                  <TableSortLabel
                    active={orderBy === 'customerNumber'}
                    direction={orderBy === 'customerNumber' ? order : 'asc'}
                    onClick={() => onRequestSort('customerNumber')}
                  >
                    Customer No
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ ...TABLE_HEADER_SX, ...TABLE_COLUMN_WIDTHS.email }}>
                  <TableSortLabel
                    active={orderBy === 'email'}
                    direction={orderBy === 'email' ? order : 'asc'}
                    onClick={() => onRequestSort('email')}
                  >
                    Email
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ ...TABLE_HEADER_SX, ...TABLE_COLUMN_WIDTHS.phone }}>
                  <TableSortLabel
                    active={orderBy === 'phone'}
                    direction={orderBy === 'phone' ? order : 'asc'}
                    onClick={() => onRequestSort('phone')}
                  >
                    Phone
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ ...TABLE_HEADER_SX, ...TABLE_COLUMN_WIDTHS.priority }}>
                  <TableSortLabel
                    active={orderBy === 'priority'}
                    direction={orderBy === 'priority' ? order : 'asc'}
                    onClick={() => onRequestSort('priority')}
                  >
                    Priority
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ ...TABLE_HEADER_SX, ...TABLE_COLUMN_WIDTHS.segment }}>
                  <TableSortLabel
                    active={orderBy === 'segment'}
                    direction={orderBy === 'segment' ? order : 'asc'}
                    onClick={() => onRequestSort('segment')}
                  >
                    Segment
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ ...TABLE_HEADER_SX, ...TABLE_COLUMN_WIDTHS.open }}>
                  Open
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.key} hover>
                  <TableCell sx={TABLE_BODY_CELL_SX}>{row.name}</TableCell>
                  <TableCell sx={TABLE_BODY_CELL_SX}>{formatValue(row.customerNumber)}</TableCell>
                  <TableCell sx={TABLE_BODY_CELL_SX}>{formatValue(row.email)}</TableCell>
                  <TableCell sx={TABLE_BODY_CELL_SX}>{formatValue(row.phone)}</TableCell>
                  <TableCell sx={TABLE_BODY_CELL_SX}>
                    <DirectoryBadge value={row.priority} />
                  </TableCell>
                  <TableCell sx={TABLE_BODY_CELL_SX}>
                    <DirectoryBadge value={row.segment} />
                  </TableCell>
                  <TableCell align="right" sx={TABLE_BODY_CELL_SX}>
                    <Button
                      component={Link}
                      to={`/customers/${encodeURIComponent(row.customerNumber)}`}
                      state={CUSTOMER_DETAIL_BACK_STATE}
                      size="small"
                      variant="text"
                      endIcon={<ArrowForwardRoundedIcon fontSize="small" />}
                      sx={{ textTransform: 'none' }}
                    >
                      Open
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Paper elevation={0} sx={{ ...SURFACE_SX, p: { xs: 2, md: 2.25 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', md: 'center' }}
        >
          <Typography variant="body2" color="text.secondary">
            {formatRangeLabel(page, rowsPerPage, totalRows)}
          </Typography>
          {totalPages > 1 && (
            <Pagination
              count={totalPages}
              page={page}
              onChange={onPageChange}
              color="primary"
              size="small"
              siblingCount={1}
              sx={{ alignSelf: { xs: 'center', md: 'auto' } }}
            />
          )}
        </Stack>
      </Paper>
    </Stack>
  );
});

export default function CustomersPage({ connected, setConnected, setError }) {
  const [customers, setCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('name');
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [segmentFilter, setSegmentFilter] = useState('');
  const deferredSearchQuery = useDeferredValue(searchQuery);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const rowsPerPage = isMobile ? MOBILE_ROWS_PER_PAGE : DESKTOP_ROWS_PER_PAGE;

  useEffect(() => {
    if (!connected) {
      setCustomers([]);
      setLoadError('');
      setIsLoading(false);
      return undefined;
    }

    let active = true;

    const loadCustomers = async () => {
      setIsLoading(true);
      setLoadError('');

      try {
        const directory = await fetchCrmCustomers();
        if (!active) {
          return;
        }
        setCustomers(directory);
      } catch (error) {
        if (!active) {
          return;
        }

        console.error('Failed to load CRM customers:', error);

        if (isCrmDisabledError(error)) {
          setError(error.message || 'CRM is disabled for this tenant');
          navigate('/', { replace: true });
          return;
        }

        if (isCrmViewBlockedError(error)) {
          setConnected(false);
          return;
        }

        setLoadError(error.message || 'Failed to load customers.');
      } finally {
        if (active) {
          setIsLoading(false);
        }
      }
    };

    loadCustomers();

    return () => {
      active = false;
    };
  }, [connected, navigate, setConnected, setError]);

  const customerRows = useMemo(() => customers.map(buildCustomerRow), [customers]);
  const priorityOptions = useMemo(
    () => buildFilterOptions(customerRows, 'priority', 'priorityLabel'),
    [customerRows]
  );
  const segmentOptions = useMemo(
    () => buildFilterOptions(customerRows, 'segment', 'segmentLabel'),
    [customerRows]
  );
  const normalizedSearchQuery = deferredSearchQuery.trim().toLowerCase();

  const filteredRows = useMemo(() => {
    return customerRows.filter((row) => {
      const matchesSearch =
        !normalizedSearchQuery || row.searchIndex.includes(normalizedSearchQuery);
      const matchesPriority = !priorityFilter || row.priority === priorityFilter;
      const matchesSegment = !segmentFilter || row.segment === segmentFilter;

      return matchesSearch && matchesPriority && matchesSegment;
    });
  }, [customerRows, normalizedSearchQuery, priorityFilter, segmentFilter]);

  const sortedRows = useMemo(() => {
    const comparator = (rowA, rowB) => {
      switch (orderBy) {
        case 'name':
          return compareSortValues(rowA.nameSort, rowB.nameSort, order);
        case 'customerNumber':
          return compareSortValues(rowA.customerNumberSort, rowB.customerNumberSort, order);
        case 'email':
          return compareSortValues(rowA.emailSort, rowB.emailSort, order);
        case 'phone':
          return compareSortValues(rowA.phoneSort, rowB.phoneSort, order);
        case 'priority':
          return compareSortValues(rowA.prioritySort, rowB.prioritySort, order);
        case 'segment':
          return compareSortValues(rowA.segmentSort, rowB.segmentSort, order);
        default:
          return 0;
      }
    };

    return [...filteredRows].sort(comparator);
  }, [filteredRows, order, orderBy]);

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / rowsPerPage));

  useEffect(() => {
    setPage(1);
  }, [normalizedSearchQuery, priorityFilter, rowsPerPage, segmentFilter]);

  useEffect(() => {
    setPage((currentPage) => Math.min(currentPage, totalPages));
  }, [totalPages]);

  const paginatedRows = useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage;
    return sortedRows.slice(startIndex, startIndex + rowsPerPage);
  }, [sortedRows, page, rowsPerPage]);

  const handleRequestSort = useCallback(
    (property) => {
      const isAsc = orderBy === property && order === 'asc';
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(property);
    },
    [order, orderBy]
  );

  const handleSearchChange = (event) => {
    const nextValue = event.target.value;
    startTransition(() => {
      setSearchQuery(nextValue);
    });
  };

  const handlePriorityFilterChange = (event) => {
    setPriorityFilter(event.target.value);
  };

  const handleSegmentFilterChange = (event) => {
    setSegmentFilter(event.target.value);
  };

  const handlePageChange = (_event, nextPage) => {
    setPage(nextPage);
  };

  if (!connected) {
    return (
      <ThemeProvider theme={CRM_THEME}>
        <Box sx={{ background: PAGE_BACKGROUND, minHeight: '100vh', py: { xs: 3, md: 4 } }}>
          <Container maxWidth="lg">
            <Stack spacing={2.25}>
              <Paper elevation={0} sx={{ ...SURFACE_SX, p: { xs: 2, md: 2.5 } }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }}>
                  <Avatar sx={{ width: 48, height: 48, bgcolor: pageColors.customers }}>
                    <PeopleAltOutlinedIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="overline" sx={{ color: 'primary.main', letterSpacing: 2.5 }}>
                      CRM
                    </Typography>
                    <Typography variant="h5" fontWeight={700}>
                      Customers
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      CRM is enabled for this tenant, but Fortnox must be connected before customer data can be loaded.
                    </Typography>
                  </Box>
                </Stack>
              </Paper>

              <Paper elevation={0} sx={{ ...SURFACE_SX, p: { xs: 2, md: 2.25 } }}>
                <Stack spacing={1.5}>
                  <TextField
                    label="Search customers"
                    value=""
                    disabled
                    size="small"
                    sx={FILTER_FIELD_SX}
                    helperText="Search stays unavailable until Fortnox is connected."
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchRoundedIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    Directory fetch is intentionally skipped while Fortnox is disconnected.
                  </Typography>
                </Stack>
              </Paper>

              <Paper elevation={0} sx={{ ...SURFACE_SX, p: { xs: 2, md: 2.5 } }}>
                <Stack spacing={1.25}>
                  <Alert severity="warning" icon={<CloudOffRoundedIcon fontSize="inherit" />}>
                    Fortnox connection required to load the CRM customer directory.
                  </Alert>
                  <Typography variant="body2" color="text.secondary">
                    Reconnect Fortnox from the home page to open the directory and customer profiles again.
                  </Typography>
                </Stack>
              </Paper>
            </Stack>
          </Container>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={CRM_THEME}>
      <Box sx={{ background: PAGE_BACKGROUND, minHeight: '100vh', py: { xs: 3, md: 4 } }}>
        <Container maxWidth="xl">
          <Stack spacing={2.25}>
            <Paper elevation={0} sx={{ ...SURFACE_SX, p: { xs: 2, md: 2.5 } }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }}>
                <Avatar sx={{ width: 48, height: 48, bgcolor: pageColors.customers }}>
                  <PeopleAltOutlinedIcon />
                </Avatar>
                <Box>
                  <Typography variant="overline" sx={{ color: 'primary.main', letterSpacing: 2.5 }}>
                    CRM
                  </Typography>
                  <Typography variant="h5" fontWeight={700}>
                    Customers
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Search the tenant&apos;s Fortnox customers and open CRM overlays without leaving the operational shell.
                  </Typography>
                </Box>
              </Stack>
            </Paper>

            <Paper elevation={0} sx={{ ...SURFACE_SX, p: { xs: 2, md: 2.25 } }}>
              <Stack
                direction={{ xs: 'column', lg: 'row' }}
                spacing={1.5}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', lg: 'flex-start' }}
              >
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} useFlexGap flexWrap="wrap">
                  <TextField
                    label="Search customers"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    size="small"
                    sx={FILTER_FIELD_SX}
                    placeholder="Name, number, email, or phone"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchRoundedIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    select
                    label="Priority"
                    value={priorityFilter}
                    onChange={handlePriorityFilterChange}
                    size="small"
                    sx={SELECT_FILTER_FIELD_SX}
                  >
                    <MenuItem value="">All priorities</MenuItem>
                    {priorityOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    label="Segment"
                    value={segmentFilter}
                    onChange={handleSegmentFilterChange}
                    size="small"
                    sx={SELECT_FILTER_FIELD_SX}
                  >
                    <MenuItem value="">All segments</MenuItem>
                    {segmentOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent={{ lg: 'flex-end' }}>
                  <Box sx={{ px: 1.25, py: 0.9, borderRadius: 2, backgroundColor: 'rgba(0,137,123,0.05)' }}>
                    <Typography variant="caption" display="block" color="text.secondary">
                      Total customers
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {customerRows.length}
                    </Typography>
                  </Box>
                  <Box sx={{ px: 1.25, py: 0.9, borderRadius: 2, backgroundColor: 'rgba(0,137,123,0.05)' }}>
                    <Typography variant="caption" display="block" color="text.secondary">
                      Matching
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {filteredRows.length}
                    </Typography>
                  </Box>
                  <Box sx={{ px: 1.25, py: 0.9, borderRadius: 2, backgroundColor: 'rgba(0,137,123,0.05)' }}>
                    <Typography variant="caption" display="block" color="text.secondary">
                      Per page
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      {rowsPerPage}
                    </Typography>
                  </Box>
                </Stack>
              </Stack>

              {isLoading && <LinearProgress sx={{ mt: 1.5, borderRadius: 999 }} />}
              {loadError && (
                <Alert severity="error" sx={{ mt: 1.5 }}>
                  {loadError}
                </Alert>
              )}
            </Paper>

            {!isLoading && !loadError && (
              <CustomerDirectoryResults
                isMobile={isMobile}
                onPageChange={handlePageChange}
                onRequestSort={handleRequestSort}
                order={order}
                orderBy={orderBy}
                page={page}
                rows={paginatedRows}
                rowsPerPage={rowsPerPage}
                totalPages={totalPages}
                totalRows={sortedRows.length}
              />
            )}
          </Stack>
        </Container>
      </Box>
    </ThemeProvider>
  );
}
